import { useMemo, useState } from 'react';
import { Image } from 'components/Image';
import {
  Box,
  Stack,
  debounce,
  ListItem,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { MobileBranding } from '../listingsMatch/Branding';
import { getFullAddress } from 'utils/listings/display';
import { formatNumber } from 'utils/Haim/utils';
import { ImageNotSupported } from '@material-ui/icons';
import usePickerListingSearch from './usePickerListingSearch';
import type { TFunction } from 'i18next';
import type { HTMLAttributes, SyntheticEvent } from 'react';
import type { AutocompleteRenderInputParams } from '@material-ui/core/Autocomplete';
import type { ListingListedFor, ProcessedListingData } from '../../../@types/sharedSchema';
import type { ListingsBlockListingFilter } from '../../../@types/signatures';

export type NewListingPickerProps = {
  loading: boolean;
  disabled?: boolean;
  // Optional Algolia filter expression ANDed into both queries — e.g.
  // 'listingListedFor:"rent"' to offer only rental listings.
  filters?: string;
  selectedListings: ProcessedListingData[];
  onSelectedListings: (listings: ProcessedListingData[]) => void;
  /**
   * Close the dropdown right after a pick. Used by single-listing-restricted
   * templates — once the one allowed listing is chosen there's nothing more
   * to select, so keeping the popper open just invites a confusing swap.
   */
  closeOnSelect?: boolean;
  /**
   * Restrict the offered listings by transaction type (the template's
   * listings-block `listingFilter`): 'sale' → only for-sale, 'rent' → only
   * rentals, 'both' → no filter. Defaults to 'both' (show everything) so any
   * caller that doesn't set it keeps the legacy unfiltered behaviour.
   */
  listedForFilter?: ListingsBlockListingFilter;
};

// The template's listings-block filter maps onto a transaction type; 'both'
// means "no transaction-type restriction", which the search layer expresses as
// an absent value.
const listedForRestriction = (
  listedFor: ListingsBlockListingFilter
): ListingListedFor | undefined => (listedFor === 'both' ? undefined : listedFor);

export function NewListingPicker({
  loading,
  disabled,
  filters,
  selectedListings,
  onSelectedListings,
  closeOnSelect = false,
  listedForFilter = 'both'
}: NewListingPickerProps) {
  const [inputValue, setInputValue] = useState('');

  const { t: translate } = useTranslation();

  // Two queries — her own listings pinned on top, then anything else she may pick (Yad2, Madlan,
  // colleagues') — with the term read as an ADDRESS, the newest term always the one shown, and the
  // market asked only once the term names something. All of it in `usePickerListingSearch`
  // (Trello #254). Ownership / office scoping of webot listings is the search backend's.
  const { listings: foundListings, loading: loadingSearch } = usePickerListingSearch(inputValue, {
    listedFor: listedForRestriction(listedForFilter)
  });

  // Keep already-selected listings in the option list even if the current search no longer
  // matches them — otherwise Autocomplete hides selected chips when their option drops out.
  const processedListings = useMemo(() => {
    let parsedResults: ProcessedListingData[] = foundListings;
    for (const selectedListing of selectedListings) {
      if (parsedResults.some((listing) => listing.id === selectedListing.id)) continue;
      parsedResults = [selectedListing, ...parsedResults];
    }
    return parsedResults;
  }, [foundListings, selectedListings]);

  const handleSelectedContactsChange = (
    _event: SyntheticEvent,
    newListings: ProcessedListingData[]
  ) => {
    onSelectedListings(newListings);
  };

  // Memoised: a debounce rebuilt on every render debounces nothing.
  const handleInputChange = useMemo(
    () =>
      debounce((_event: SyntheticEvent, newInputValue: string) => {
        setInputValue(newInputValue);
      }, 200),
    []
  );

  const showLoading = loading || loadingSearch;

  return (
    <Autocomplete
      multiple
      // Keep the popper open after each pick so the realtor can add
      // several listings in one go without reopening the dropdown
      // between each click. They close it themselves (Escape / click
      // outside / blur) when they're done selecting. Single-listing
      // templates flip this off — one pick and the dropdown closes.
      disableCloseOnSelect={!closeOnSelect}
      blurOnSelect={closeOnSelect}
      disabled={disabled}
      value={selectedListings}
      loading={showLoading}
      options={processedListings}
      onInputChange={handleInputChange}
      loadingText={translate('signatureSend.loadingListings')}
      noOptionsText={translate('signatureSend.noListingsFound')}
      onChange={handleSelectedContactsChange}
      renderInput={handleRenderInput(translate, showLoading)}
      renderOption={handleRenderOption(translate)}
      filterOptions={(_) => _}
      getOptionLabel={handleGetOptionLabel(processedListings)}
      isOptionEqualToValue={handleIsOptionEqualToValue}
    />
  );
}

export function handleRenderInput(
  translate: TFunction,
  loading: boolean,
  label: string | null = null
) {
  return (params: AutocompleteRenderInputParams): JSX.Element => {
    return (
      <TextField
        {...params}
        label={label ?? translate('signatureSend.selectListing')}
        InputProps={{
          ...params.InputProps,
          endAdornment: loading ? (
            <CircularProgress color="inherit" size={20} />
          ) : (
            params.InputProps.endAdornment
          )
        }}
      />
    );
  };
}

/**
 * Street display for picker rows/chips: keep whatever the street text already
 * carries (house number, apartment markers like "דירה 4") and append the
 * structured `houseNumber` only when the text doesn't already end with a
 * number — so the realtor can tell two listings on the same street apart.
 */
export function streetWithHouseNumber(street?: string | null, houseNumber?: number | null): string {
  const trimmed = (street || '').trim();
  if (!trimmed) return '';
  if (/\d\s*$/.test(trimmed) || !houseNumber) return trimmed;
  return `${trimmed} ${houseNumber}`;
}

/**
 * "קומה X" segment for picker rows/chips — floor 0 (ground) included.
 * `translate` is optional so the chip-label builder (no i18n context at its
 * call site) still renders the Hebrew word the app uses everywhere.
 */
export function floorLabel(floor?: number | null, translate?: TFunction): string {
  if (floor == null || Number.isNaN(floor)) return '';
  const word = translate ? translate('listingsMatch.floor') : 'קומה';
  return `${word} ${floor}`;
}

/**
 * "דירה X" segment for picker rows/chips. Same optional-`translate` contract
 * as {@link floorLabel} so the chip-label builder (no i18n context) still
 * renders the Hebrew word the app uses everywhere.
 */
export function apartmentLabel(
  apartmentNumber?: number | null,
  translate?: TFunction
): string {
  if (apartmentNumber == null || Number.isNaN(apartmentNumber)) return "";
  const word = translate ? translate("listingsMatch.apartment") : "דירה";
  return `${word} ${apartmentNumber}`;
}

export function handleGetOptionLabel(_processedListings?: ProcessedListingData[]) {
  return ({ address, price }: ProcessedListingData): string => {
    if (!address) return '';

    const { street, houseNumber, apartmentNumber, neighborhood, city, floor } = address;

    let addressPart: string[];

    if (street)
      addressPart = [
        streetWithHouseNumber(street, houseNumber),
        apartmentLabel(apartmentNumber),
        floorLabel(floor),
        city
      ];
    else addressPart = [neighborhood, apartmentLabel(apartmentNumber), floorLabel(floor), city];

    const parsedAddressPart = addressPart.filter(Boolean).join(', ');
    const parsedPricePart = price > 0 ? ` (₪${formatNumber(price)})` : '';

    return `${parsedAddressPart}${parsedPricePart}`;
  };
}

export function handleIsOptionEqualToValue(
  option: ProcessedListingData,
  value: ProcessedListingData
): boolean {
  if (!option || !value) return false;

  if (option.id && value.id) return option.id === value.id;

  if ('objectID' in option && 'objectID' in value) return option.objectID === value.objectID;

  if (option.address && value.address) {
    const optionAddr = option.address;
    const valueAddr = value.address;

    return (
      optionAddr.street === valueAddr.street &&
      optionAddr.city === valueAddr.city &&
      optionAddr.neighborhood === valueAddr.neighborhood
    );
  }

  return false;
}

/**
 * Slim row content for one listing — thumbnail + address + price + a thin
 * caption line of rooms/floor/sqm + the origin icon inline. Shared by the
 * Autocomplete dropdown rows AND the favorites modal so both stay
 * visually consistent. Caller wraps in whatever container they need
 * (ListItem for the dropdown, a checkbox-prefixed row for the modal).
 */
export function CondensedListingRowContent({
  listing,
  translate
}: {
  listing: ProcessedListingData;
  translate: TFunction;
}): JSX.Element {
  const { address, price, roomsCount, squareMeter, imageUrls, listingOrigin } = listing;
  const { street, houseNumber, apartmentNumber, city, neighborhood, floor } = address ?? {};

  // Show the full street INCLUDING the house number (and any apartment text
  // embedded in it) + apartment number + floor — picking the right contract
  // listing needs the exact address, unlike browse surfaces that redact it.
  const streetPart = [
    streetWithHouseNumber(street, houseNumber),
    apartmentLabel(apartmentNumber, translate),
    floorLabel(floor, translate)
  ]
    .filter(Boolean)
    .join(', ');
  const fullAddress = getFullAddress({
    address: streetPart,
    neighborhood,
    city
  });
  const showPicture = imageUrls && imageUrls.length > 0;

  return (
    <>
      <Box
        sx={{
          width: 56,
          height: 56,
          flexShrink: 0,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.neutral',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {showPicture ? (
          <Image
            src={imageUrls[0]}
            alt={fullAddress}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <ImageNotSupported sx={{ color: 'text.disabled', fontSize: 24 }} />
        )}
      </Box>
      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {listingOrigin && <MobileBranding listingOrigin={listingOrigin} />}
          <Typography variant="subtitle2" noWrap sx={{ flex: 1, minWidth: 0 }}>
            {fullAddress}
          </Typography>
          {price > 0 && (
            <Typography variant="subtitle2" color="text.primary" sx={{ flexShrink: 0 }}>
              ₪{formatNumber(price)}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ color: 'text.secondary' }}>
          {roomsCount > 0 && (
            <Typography variant="caption">
              {`${roomsCount} ${translate('listingsMatch.rooms')}`}
            </Typography>
          )}
          {squareMeter > 0 && (
            <Typography variant="caption">
              {`${squareMeter} ${translate('listingsMatch.sqrMeter')}`}
            </Typography>
          )}
        </Stack>
      </Stack>
    </>
  );
}

export function handleRenderOption(translate: TFunction) {
  return (props: HTMLAttributes<HTMLLIElement>, listing: ProcessedListingData) => {
    return (
      <ListItem {...props} key={listing.id} sx={{ py: 0.75, px: 1.5, gap: 1.25 }}>
        <CondensedListingRowContent listing={listing} translate={translate} />
      </ListItem>
    );
  };
}
